import ColorScheme from 'owa-color-utils/lib/ColorScheme';
import { getOptimalTextColorString } from 'owa-color-utils/lib/colorUtils';

const defaultMinimumContrast = 4.5;

/**
 * Cache of calendars assigned with colors
 */
let optimalColorCache: { [key: string]: string } = {};

/**
 * Calculates the optimal text color on the given background from the given color scheme, defaulting to the desired color if it
 * passes the minimum contrast checks. Caches the values calculated for future calculation
 * @param backgroundColor the background color
 * @param colorScheme the color scheme
 * @param desiredColor the desired color to use, will be default if accessible
 * @param minimumContrast the contrast to use, defaults to 4.5
 */
export function getOptimalTextColor(
    backgroundColor: string,
    colorScheme: ColorScheme,
    desiredColor: string,
    minimumContrast: number = defaultMinimumContrast
): string {
    let cacheResult = getOptimalColorFromCache(
        backgroundColor,
        colorScheme,
        desiredColor,
        minimumContrast
    );

    if (cacheResult) {
        // if we have already ran this computation in the past, return the cached value
        return cacheResult;
    }

    let result = getOptimalTextColorString(
        backgroundColor,
        colorScheme,
        desiredColor,
        minimumContrast
    );
    addOptimalColorToCache(result, backgroundColor, colorScheme, desiredColor, minimumContrast);

    return result;
}

function getOptimalColorFromCache(
    backgroundColor: string,
    colorScheme: ColorScheme,
    desiredColor: string,
    minimumContrast: number
): string {
    let cacheKey = createCacheKey(backgroundColor, colorScheme, desiredColor, minimumContrast);
    return optimalColorCache[cacheKey];
}

function addOptimalColorToCache(
    optimalColor: string,
    backgroundColor: string,
    colorScheme: ColorScheme,
    desiredColor: string,
    minimumContrast: number
): void {
    let cacheKey = createCacheKey(backgroundColor, colorScheme, desiredColor, minimumContrast);
    optimalColorCache[cacheKey] = optimalColor;
}

function createCacheKey(
    backgroundColor: string,
    colorScheme: ColorScheme,
    desiredColor: string,
    minimumContrast: number
): string {
    return (
        backgroundColor + '_' + colorScheme.calPrimary + '_' + desiredColor + '_' + minimumContrast
    );
}
